import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { CustomerBillingPermission, UpdateCustomerBodyDto, UpdateSubscriptionBodyDto } from '@picflow/types';
import { StripeCustomerDto } from '@picflow/types/billing/customer.dto';
import * as Sentry from '@sentry/angular';
import { PaymentIntent, StripeError } from '@stripe/stripe-js';

import { lastValueFrom } from 'rxjs';
import type { Stripe } from 'stripe';
import { Proration } from '../account/dialogs/change-plan-dialog/change-plan-dialog.component';
import { GbForStoragePrice, msInADay, trueUpDurationInDays } from '../consts';
import { convertPermissions } from '../lib/convert-permissions';
import { AppConfigService } from '../providers/config.service';
import { PlanState } from '../types/plan-state.type';
import { Discount, Plan, PlanPrice, PlanProduct, StorageAddon } from '../types/plan.type';
import { ProductPrice } from '../types/product.type';
import { Promotion } from '../types/promotion.type';
import { SubscriptionLimits } from '../types/subscription-limits.type';
import { AnalyticsService } from './analytics.service';
import { DataLoaderService } from './data-loader.service';
import { FeatureFlagsService } from './feature-flags.service';
import { PricingService } from './pricing.service';
import { PromotionService } from './promotion.service';
import { StateService } from './state.service';
import { StripeService } from './stripe.service';
import { ToastService } from './toast.service';

export type changePlanResp = Promise<boolean | { error: StripeError; retry: () => boolean | changePlanResp }>;

@Injectable({
  providedIn: 'root',
})
export class SubscriptionService {
  constructor(
    private config: AppConfigService,
    private http: HttpClient,
    private pricingService: PricingService,
    private state: StateService,
    private loader: DataLoaderService,
    private analyticsService: AnalyticsService,
    private promotionService: PromotionService,
    private toasts: ToastService,
    private featureFlags: FeatureFlagsService,
    private stripeService: StripeService
  ) {}

  private async getSubscription() {
    const stripeSub = await lastValueFrom(
      this.http.get<Stripe.Subscription>(`${this.config.apiEndpoint}/billing/subscriptions`)
    );

    return {
      ...stripeSub,
      promo: await this.getPlanPromo(stripeSub),
    };
  }

  private async getPlanPromo(stripeSub: Stripe.Subscription) {
    const promoCode = stripeSub.discount?.promotion_code as Stripe.PromotionCode;
    const currentPromoCode = promoCode?.code;
    if (currentPromoCode) {
      return await this.promotionService.checkCode(promoCode?.code);
    }

    if (stripeSub.discount) {
      // This is needed for when a promotion doesn't have a code
      return this.promotionService.convertPromo(stripeSub.discount);
    }
  }

  private findPlanPriceItem(items: Stripe.SubscriptionItem[]): Stripe.SubscriptionItem {
    const item = items.find(i => {
      const lookupKey: string = i.price.lookup_key;
      if (lookupKey.startsWith('pro_')) return true;
      if (lookupKey.startsWith('team_')) return true;
      if (lookupKey.startsWith('plus_')) return true;
      if (lookupKey.startsWith('basic_')) return true;
      if (lookupKey.startsWith('free_')) return true;
      if (lookupKey.startsWith('enterprise_')) return true;
      return false;
    });
    if (!item)
      setTimeout(() => {
        throw new Error('Could not find plan price');
      });
    return item || items[0];
  }

  public async getPlan(): Promise<Plan> {
    this.loader.removeFuzzy('billingCustomer');
    this.loader.remove('plan-name_');
    this.loader.remove('scheduledUpdates');
    this.loader.removeFuzzy('galleries');
    this.loader.removeFuzzy('plan-name_');

    const [stripeSub, limits, customer, scheduledDowngradeDate] = await Promise.all([
      this.getSubscription(),
      this.getSubscriptionLimits(),
      this.getBillingCustomer(),
      this.getScheduledDowngradeDate(),
    ]);

    const planPriceItem = this.findPlanPriceItem(stripeSub.items.data);
    const planPrice = planPriceItem.price;
    const planProduct = planPrice.product as Stripe.Product;

    const price: PlanPrice = {
      id: planPrice.id,
      lookupKey: planPrice.lookup_key,
      amount: planPrice.unit_amount / 100,
      billingFrequency: planPrice.recurring.interval === 'month' ? 'monthly' : 'yearly',
    };

    let name = planProduct.name;
    if (name.includes('Picflow ')) name = name.split('Picflow ')[1];

    const product: PlanProduct = {
      id: planProduct.id,
      isFree: planProduct.name === 'Picflow Free',
      isMax: ['Picflow Pro', 'Picflow Team', 'Picflow Enterprise'].includes(planProduct.name),
      name,
    };

    // const _price = planPrices.find(price => subscribedProductIds.includes(price.id));
    // const product: Product = products.find(product => product.prices.find(price => price.id === _price.id));
    // const price: ProductPrice = product.prices.find(price => price.id === _price.id);

    const _storageAddon = stripeSub.items.data.find(p => p.price.lookup_key.includes('storage'));
    let storageAddon: StorageAddon;

    if (_storageAddon) {
      const storageAddonProduct = _storageAddon.price.product as Stripe.Product;
      storageAddon = {
        id: _storageAddon.price.id,
        amount: GbForStoragePrice(_storageAddon.price),
        quantity: _storageAddon.quantity,
        unitCost: _storageAddon.price.unit_amount,
        productId: storageAddonProduct.id,
      };
    }

    let total = stripeSub.items.data.reduce((t, item) => t + item.price.unit_amount * item.quantity, 0);
    const subTotal = total;
    let discount: Discount;

    if (stripeSub.discount && stripeSub.promo) {
      discount = {
        start: stripeSub.discount.start * 1000,
        end: stripeSub.discount.end * 1000,
        promo: stripeSub.promo,
      };
      const discountAmount = discount.promo?.amountOff || (price.amount / 100) * discount.promo.percentOff;
      if (this.promotionService.appliesTo(discount.promo, product)) {
        total -= discountAmount * 100;
      }
    }

    const isEnterprise = price.lookupKey.startsWith('enterprise_');

    const latestInvoice = stripeSub.latest_invoice as Stripe.Invoice;
    const plan: Plan = {
      latestInvoicePeriodEnd: latestInvoice?.period_end * 1000,
      status: stripeSub.status,
      subscriptionId: stripeSub.id,
      tenantId: this.state.activeTenant.id,
      product,
      limits: limits,
      price,
      willRenew: !scheduledDowngradeDate,
      billingPeriodEnd: stripeSub.current_period_end * 1000,
      total,
      subTotal,
      storageAddon,
      trialEnd: stripeSub.trial_end * 1000,
      discount,
      // previousPromoCode: stripeSub.previousPromoCode,
      isEnterprise,
      mainPriceQuantity: planPriceItem.quantity,
      hostedInvoiceUrl: latestInvoice?.hosted_invoice_url,
    };

    if (isEnterprise) {
      plan.nextTrueUpDate = this.getNextTrueUpPeriodEndDate(plan.billingPeriodEnd);
      const daysRemainingUntilTrueUpEnd = (plan.nextTrueUpDate - Date.now()) / msInADay;
      plan.inTrueUp = daysRemainingUntilTrueUpEnd < trueUpDurationInDays;
    }

    if (this.featureFlags.get('testPastDue')) plan.status = 'past_due';

    this.state.plan = plan;
    this.state.stripeCustomerId = customer.customer.id;
    this.state.balance = -customer.customer.balance;
    this.state.stripeCustomer = customer.customer;

    if (this.state.user) this.analyticsService.identify();

    return plan;
  }

  public async handlePayment(payment_intent: PaymentIntent) {
    if (payment_intent.status !== 'requires_action') return true;
    const { error } = await this.confirmCardPayment(payment_intent.client_secret);

    if (!error) return true;
    console.error(error);
    // Sentry.captureException('failed_to_confirm_3d_secure_subscription_update');
    this.toasts.add('Payment confirmation failed', 'error');
    return {
      error,
      retry: async () => {
        return await this.handlePayment(payment_intent);
      },
    };
  }

  public async confirmCardPayment(secret: string) {
    return this.stripeService.confirmCardPayment(secret, {
      payment_method: this.state.paymentMethod,
    });
  }

  private async updateSubscription(params: UpdateSubscriptionBodyDto) {
    const resp = await lastValueFrom(
      this.http.patch<Stripe.Subscription>(`${this.config.apiEndpoint}/billing/subscriptions`, params)
    );
    if (!(resp.latest_invoice as Stripe.Invoice).payment_intent) return true;
    return this.handlePayment((resp.latest_invoice as Stripe.Invoice).payment_intent as unknown as PaymentIntent);
  }

  public async changePromotion(promotion_code: string) {
    return await this.updateSubscription({ promotion_code });
  }

  public async changePlan(price: ProductPrice, promo?: Promotion, startTrial = false): changePlanResp {
    if (promo && promo.id !== this.state.plan.discount?.promo.id) {
      try {
        await this.changePromotion(promo.code);
      } catch (err) {
        if (!(err instanceof HttpErrorResponse)) throw err;
        if (!err.error?.message) throw err;
        this.toasts.add(err.error.message, 'error');
        return false;
      }
      await lastValueFrom(this.http.get(`${this.config.apiEndpoint}/billing/subscriptions`)); // need to get the new subscription, otherwise the new promo won't be applied
      await this.getProration(price, promo); // need to get the new proration, otherwise the new promo won't be applied
    }
    const prices: { id: string; quantity?: number }[] = [{ id: price.id }];
    if (this.state.plan.storageAddon) {
      const storageAddonPrices = await this.pricingService.getPrices('storage_addon');
      const storageAddonPrice = storageAddonPrices.find(
        _price =>
          _price.billingFrequency === price.billingFrequency &&
          _price.product === this.state.plan.storageAddon.productId
      );
      if (this.state.plan.storageAddon.quantity) {
        prices.push({ id: storageAddonPrice.id, quantity: this.state.plan.storageAddon.quantity });
      }
    }
    try {
      const trialLength = this.pricingService.getTrialLengthDays();
      return await this.updateSubscription({ prices, trial_days: startTrial ? trialLength : undefined });
    } catch (err) {
      const message = ((): string => {
        if (!err?.error?.message)
          return 'There was an error charging your card. Please update your payment method or contact support';
        const error: string = err.error.message;
        if (error.includes('requires additional user action'))
          return 'There was an error charging your card. Please update your payment method or contact support';
        return err.error?.message;
      })();
      // const message = err?.error?.message || 'There was an error charging your card.';
      this.toasts.add(message, 'error', { duration: 5 * 1000 });
      console.error(err);
      Sentry.captureMessage('change-plan-failed', { level: 'info' });
      return false;
    }
  }

  public async changeAddonCount(priceId: string, quantity: number) {
    const resp = await lastValueFrom(
      this.http.get<Stripe.Subscription>(`${this.config.apiEndpoint}/billing/subscriptions`)
    );

    const prices = resp.items.data.map(item => ({
      id: item.price.id,
      quantity: item.quantity,
    }));

    const price = prices.find(p => p.id === priceId);
    if (price) price.quantity = quantity;
    else prices.push({ id: priceId, quantity });

    prices.forEach(price => {
      if (price.quantity === 0) {
        price['deleted'] = true;
      }
    });

    await lastValueFrom(this.http.patch(`${this.config.apiEndpoint}/billing/subscriptions`, { prices }));
  }

  public async cancel() {
    await lastValueFrom(this.http.delete(`${this.config.apiEndpoint}/billing/subscriptions`));
  }

  public async reActivate() {
    const resp = await lastValueFrom(
      this.http.get<Stripe.Subscription>(`${this.config.apiEndpoint}/billing/subscriptions`)
    );
    const prices = resp.items.data.map(item => ({
      id: item.price.id,
      quantity: item.quantity,
    }));
    await lastValueFrom(this.http.patch(`${this.config.apiEndpoint}/billing/subscriptions`, { prices }));
  }

  public async getProration(price: ProductPrice, promo?: Promotion): Promise<Proration> {
    const prices: { id: string; quantity?: number }[] = [{ id: price.id }];
    if (this.state.plan.storageAddon) {
      const storageAddonPrices = await this.pricingService.getPrices('storage_addon');
      const storageAddonPrice = storageAddonPrices.find(
        _price =>
          _price.billingFrequency === price.billingFrequency &&
          _price.product === this.state.plan.storageAddon.productId
      );
      prices.push({ id: storageAddonPrice.id, quantity: this.state.plan.storageAddon.quantity });
    }

    const [resp, mainPrices] = await Promise.all([
      lastValueFrom(
        this.http.patch<Stripe.Invoice>(`${this.config.apiEndpoint}/billing/subscriptions/prorate`, {
          prices,
          promotion_code: promo?.code,
        })
      ),
      this.pricingService.getPrices('main'),
    ]);

    if (!resp) return undefined;

    const allLines = resp.lines.data;

    // resp.lines = resp.lines.data;

    allLines.sort((a, b) => {
      const aIsMain = mainPrices.find(p => p.id === a.price.id);
      const bIsMain = mainPrices.find(p => p.id === b.price.id);
      if (aIsMain && !bIsMain) return -1;
      if (bIsMain && !aIsMain) return 1;
      if (a.type === 'subscription') return -1;
      if (b.type === 'subscription') return 1;
      return 0;
    });

    const products = [...new Set(allLines.map(line => line.price.product))];

    const lines = [];
    products.forEach(product => {
      allLines.filter(line => line.price.product === product).forEach(line => lines.push(line));
    });

    const subTotal = lines.reduce((total, line) => total + line.amount, 0);
    const usedBalance = -((resp.starting_balance || 0) - (resp.ending_balance || 0));
    const totalDiscount = lines.reduce((total, line) => total + line.discount_amounts[0]?.amount || 0, 0);

    let prorationDiscount: { start: number; end: number; promo: Promotion };

    for (const line of lines) {
      if (!line.discount_amounts[0]?.amount) continue; // we don't want to show a discount that has no impact on the invoice
      const discount = line.discount_amounts[0]?.discount;
      if (discount) {
        prorationDiscount = {
          start: discount.start * 1000,
          end: discount.end * 1000,
          promo: this.promotionService.convertPromo(discount),
        };
        break;
      }
    }

    return {
      ...resp,
      lines,
      subTotal,
      usedBalance,
      totalDiscount,
      discount: prorationDiscount,
    };
  }

  public async startTrial(price: ProductPrice, promo?: Promotion) {
    const proration = await this.getProration(price, promo);
    if (proration.total > 0) throw 'cant-start-trial:proration';
    await lastValueFrom(
      this.http.patch(`${this.config.apiEndpoint}/billing/subscriptions`, {
        prices: [price],
        promotion_code: promo?.code,
      })
    );
  }

  public async getSubscriptionLimits(): Promise<SubscriptionLimits> {
    const resp = await this.loader.get('billingCustomer', () => this.getBillingCustomer());
    const permissions = resp.billing_permissions;
    // let galleryLimit = resp.billing_permissions.galleries_limit;
    // if (galleryLimit === -1) galleryLimit = 10000000;
    let maxTenantSeats = resp.billing_permissions.max_tenant_seats ?? -1;
    if (maxTenantSeats === -1) maxTenantSeats = 10000000;

    return convertPermissions(maxTenantSeats, permissions.scopes, permissions);
  }

  public async getBillingCustomer(includeTaxId = false): Promise<{
    customer: StripeCustomerDto;
    billing_permissions: CustomerBillingPermission;
  }> {
    const key = 'billingCustomer' + (includeTaxId ? '_with-tax-id' : '');
    return await this.loader.get(key, () => this.getBillingCustomerInternal(includeTaxId));
  }

  public async updateBillingInfo(params: UpdateCustomerBodyDto) {
    const resp = await lastValueFrom(this.http.patch(`${this.config.apiEndpoint}/billing/customer`, params));
    this.loader.removeFuzzy('billingCustomer');
    this.getPlan();
    return resp;
  }

  public getPlanState(): PlanState {
    if (this.state.plan.isEnterprise) return 'active';
    if (this.state.plan.price.amount === 0) return 'free';
    if (this.state.plan.status === 'past_due' && !this.state.temporarilyHideUpgradeCta) return 'past-due';
    if (this.state.plan.status === 'trialing') {
      if (this.state.plan.willRenew) return 'trialing-will-renew';
      return 'trialing-wont-renew';
    }
    if (this.state.plan.willRenew) return 'active';
    return 'pending-termination';
  }

  public async getScheduledDowngradeDate() {
    const update = await this.getPendingUpdates();
    if (!update) return;
    const lastPhase = update.phases.pop();
    const isDowngradeToFree = (lastPhase.items[0].price as Stripe.Price).unit_amount === 0;
    if (!isDowngradeToFree) return;
    return lastPhase.end_date * 1000;
  }

  public async getPendingUpdates() {
    return await this.loader.get('scheduledUpdates', () => this.getPendingUpdatesInternal());
  }

  private async getBillingCustomerInternal(includeTaxId: boolean) {
    const url = `${this.config.apiEndpoint}/billing/customer${includeTaxId ? '?expand[]=tax_ids' : ''}`;
    return await lastValueFrom(
      this.http.get<{
        customer: StripeCustomerDto;
        billing_permissions: CustomerBillingPermission;
      }>(url)
    );
  }

  private async getPendingUpdatesInternal() {
    try {
      return await lastValueFrom(
        this.http.get<Stripe.SubscriptionSchedule>(`${this.config.apiEndpoint}/billing/subscription_schedules`)
      );
    } catch (err) {
      if (!(err instanceof HttpErrorResponse)) throw err;
      if (err.status !== 404) throw err;
      return undefined;
    }
  }

  /**
   * Returns the next true up period end date based on the billing period end date
   */
  private getNextTrueUpPeriodEndDate(billingPeriodEndMs: number): number {
    const billingPeriodEndDate = new Date(billingPeriodEndMs);
    // removing three months from the billing period end date
    const monthsBetweenTrueUps = 3;
    const threeMonthsBefore = new Date(billingPeriodEndDate);
    threeMonthsBefore.setMonth(threeMonthsBefore.getMonth() - monthsBetweenTrueUps);
    // if the date is in the past, we return the billing period end date
    const dateIsInThePast = threeMonthsBefore.getTime() < Date.now();
    if (dateIsInThePast) return billingPeriodEndMs;
    // otherwise we recursively call this function with the new date
    return this.getNextTrueUpPeriodEndDate(threeMonthsBefore.getTime());
  }
}
